import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

// Reprise fidèle de l'écran « preview » de la modale de génération de l'app
// (web/src/app/(dashboard)/programs/ProgramsPageClient.tsx, phase "preview").
// Le coach voit ici exactement ce qu'il verra dans le produit — mêmes statistiques,
// même graphe de volume, mêmes séances dépliables. Rien n'est réinventé pour la
// landing : c'est le produit, pas une vitrine.

const DAY_SHORT = ["", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const PERIODIZATION_COLORS = ["#4F6EF7", "#F97316", "#10B981", "#8B5CF6"];

// Le catalogue d'exercices stocke les muscles en anglais : on traduit à l'affichage,
// et chaque muscle a sa couleur — sinon les barres se ressemblent toutes.
const MUSCLE_FR: Record<string, string> = {
  chest: "Pectoraux", back: "Dos", "middle back": "Dos", "lower back": "Dos",
  quads: "Quadriceps", quadriceps: "Quadriceps",
  hamstrings: "Ischio-jamb.", glutes: "Fessiers", shoulders: "Épaules",
  biceps: "Biceps", triceps: "Triceps", core: "Core", calves: "Mollets",
  lats: "Dorsaux", traps: "Trapèzes", forearms: "Avant-bras",
  abdominals: "Abdominaux", abs: "Abdominaux",
};

const MUSCLE_COLOR: Record<string, string> = {
  chest: "#2563EB", back: "#2563EB", lats: "#2563EB", quads: "#F97316",
  hamstrings: "#EAB308", glutes: "#EAB308", shoulders: "#8B5CF6",
  biceps: "#EC4899", triceps: "#F59E0B", core: "#10B981",
  calves: "#14B8A6", traps: "#64748B", forearms: "#94A3B8", abs: "#10B981",
};

const muscleLabel = (mg: string) => MUSCLE_FR[mg] ?? mg.charAt(0).toUpperCase() + mg.slice(1);
const muscleColor = (mg: string) => MUSCLE_COLOR[mg] ?? "#94A3B8";

export interface PreviewExercise {
  exercise_name: string;
  sets_target: number;
  reps_min: number;
  reps_max: number;
  rpe_target: number;
  rest_seconds: number;
  muscle_groups?: string[];
  primary_muscles?: string[];
  secondary_muscles?: string[];
}

export interface PreviewSession {
  week_number: number;
  day_of_week: number;
  name: string;
  estimated_duration_min: number;
  exercises: PreviewExercise[];
}

export interface PreviewBlock {
  index: number;
  weeks_from: number;
  weeks_to: number;
  focus: string;
  base_rpe: number;
}

export interface PreviewProgram {
  name: string;
  description: string;
  periodization_blocks?: PreviewBlock[];
  sessions: PreviewSession[];
}

function blockForWeek(blocks: PreviewBlock[], week: number): PreviewBlock {
  return (
    blocks.find((b) => week >= b.weeks_from && week <= b.weeks_to) ??
    blocks[0] ?? { index: 1, weeks_from: week, weeks_to: week, focus: "Bloc", base_rpe: 7 }
  );
}

function blockColor(block: PreviewBlock) {
  return PERIODIZATION_COLORS[(block.index - 1) % PERIODIZATION_COLORS.length];
}

const ProgramPreview = ({ program }: { program: PreviewProgram }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const weekNumbers = useMemo(
    () => [...new Set(program.sessions.map((s) => s.week_number))].sort((a, b) => a - b),
    [program],
  );

  const [selectedWeek, setSelectedWeek] = useState(weekNumbers[0] ?? 1);

  const blocks = program.periodization_blocks ?? [];
  const colorOfWeek = (w: number) => blockColor(blockForWeek(blocks, w));
  const B1 = PERIODIZATION_COLORS[0];
  const B2 = PERIODIZATION_COLORS[1];

  const stats = useMemo(() => {
    const allEx = program.sessions.flatMap((s) => s.exercises);
    const uniqueExercises = new Set(allEx.map((e) => e.exercise_name)).size;
    const sessionsWk1 = program.sessions.filter((s) => s.week_number === 1).length;
    const avgDuration = program.sessions.length
      ? Math.round(program.sessions.reduce((sum, s) => sum + s.estimated_duration_min, 0) / program.sessions.length)
      : 0;

    const totalSetsAll = allEx.reduce((sum, e) => sum + e.sets_target, 0);
    const avgRPE = allEx.length ? allEx.reduce((sum, e) => sum + e.rpe_target, 0) / allEx.length : 7;
    const avgRestSec = allEx.length ? allEx.reduce((sum, e) => sum + e.rest_seconds, 0) / allEx.length : 90;
    const totalMin = program.sessions.reduce((sum, s) => sum + s.estimated_duration_min, 0);
    const density = totalMin > 0 ? totalSetsAll / totalMin : 0;

    // Volume (séries) et RPE moyen par semaine
    const weekVolumes = weekNumbers.map((w) => {
      const wEx = program.sessions.filter((s) => s.week_number === w).flatMap((s) => s.exercises);
      return {
        week: w,
        sets: wEx.reduce((sum, e) => sum + e.sets_target, 0),
        avgRPE: wEx.length ? wEx.reduce((sum, e) => sum + e.rpe_target, 0) / wEx.length : 7,
      };
    });
    const maxVolume = Math.max(...weekVolumes.map((w) => w.sets), 1);

    // Graphe SVG (viewBox 0 0 100 52)
    const n = weekVolumes.length;
    const barGap = n > 8 ? 0.5 : 1.5;
    const barW = (100 - (n - 1) * barGap) / n;
    const maxBarH = 38;
    const minBarH = 4;
    const RPE_MIN = 5;
    const RPE_MAX = 10;

    const chartBars = weekVolumes.map((v, i) => ({
      x: i * (barW + barGap),
      h: Math.max(minBarH, (v.sets / maxVolume) * maxBarH),
      rpe: v.avgRPE,
      sets: v.sets,
    }));
    const rpePts = chartBars.map((b) => ({
      x: b.x + barW / 2,
      y: 52 - ((b.rpe - RPE_MIN) / (RPE_MAX - RPE_MIN)) * maxBarH - 4,
    }));
    const rpeLineD = n > 1
      ? rpePts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")
      : "";

    // Volume par groupe musculaire, normalisé par semaine.
    // Le repli sur muscle_groups est PAR EXERCICE : un repli global (« si aucun
    // exercice n'a de primary_muscles ») excluait silencieusement du calcul tous
    // les exercices non renseignés dès qu'un seul l'était — d'où des volumes
    // musculaires très en dessous de la réalité.
    const nWeeks = weekNumbers.length || 1;
    const primary = new Map<string, number>();
    const secondary = new Map<string, number>();
    for (const ex of allEx) {
      const primaries = ex.primary_muscles?.length ? ex.primary_muscles : (ex.muscle_groups ?? []);
      for (const mg of primaries) {
        const k = mg.toLowerCase();
        primary.set(k, (primary.get(k) ?? 0) + ex.sets_target);
      }
      for (const mg of ex.secondary_muscles ?? []) {
        const k = mg.toLowerCase();
        secondary.set(k, (secondary.get(k) ?? 0) + ex.sets_target);
      }
    }
    const topPrimary = [...primary.entries()]
      .sort((a, b) => b[1] - a[1]).slice(0, 5)
      .map(([k, v]) => [k, Math.round(v / nWeeks)] as [string, number]);
    const topSecondary = [...secondary.entries()]
      .filter(([k]) => !primary.has(k))
      .sort((a, b) => b[1] - a[1]).slice(0, 5)
      .map(([k, v]) => [k, Math.round(v / nWeeks)] as [string, number]);

    return {
      uniqueExercises, sessionsWk1, avgDuration, chartBars, rpePts, rpeLineD, barW,
      topPrimary, topSecondary, totalSetsAll, avgRPE, avgRestSec, density,
    };
  }, [program, weekNumbers]);

  const maxPrimary = stats.topPrimary[0]?.[1] ?? 1;
  const maxSecondary = stats.topSecondary[0]?.[1] ?? 1;
  const perWeek = weekNumbers.length > 1 ? "/sem" : "";

  return (
    <div className="app-theme overflow-hidden rounded-3xl border border-outline-variant/25"
      style={{ background: "hsl(var(--surface))" }}>

      {/* Titre + description */}
      <div className="px-4 pt-5 pb-4 md:px-7 md:pt-6">
        <h3 className="mb-1.5 text-xl font-extrabold text-on-surface md:text-2xl">{program.name}</h3>
        {program.description && (
          <p className="max-w-2xl text-sm leading-relaxed text-on-surface-variant">{program.description}</p>
        )}
      </div>

      {/* Stats + périodisation */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3 border-b border-outline-variant/20 px-4 pb-5 md:gap-8 md:px-7">
        {[
          { label: "DURÉE", value: `${weekNumbers.length} sem` },
          { label: "CADENCE", value: `${stats.sessionsWk1} ×/sem` },
          { label: "EXERCICES", value: String(stats.uniqueExercises) },
          { label: "SÉANCE MOY.", value: `${stats.avgDuration} min` },
        ].map(({ label, value }) => (
          <div key={label}>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60">{label}</p>
            <p className="text-xl font-extrabold text-on-surface md:text-2xl">{value}</p>
          </div>
        ))}

        {blocks.length > 0 && (
          <div className="pb-0.5 md:ml-auto">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60">Périodisation</p>
            <div className="flex flex-wrap gap-2">
              {blocks.map((block) => {
                const color = blockColor(block);
                return (
                  <span key={`${block.index}-${block.weeks_from}`}
                    className="rounded-full px-3 py-1.5 text-xs font-semibold"
                    style={{ backgroundColor: `${color}18`, color }}>
                    Bloc {block.index} · Sem {block.weeks_from}–{block.weeks_to}
                    {block.focus ? ` · ${block.focus}` : ""}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row">

        {/* ── Volume + séances ── */}
        <div className="min-w-0 flex-1 space-y-5 px-4 py-5 md:px-7">

          {/* Graphe de volume */}
          <div className="rounded-2xl border border-outline-variant/20 px-3 py-3 md:px-5 md:py-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold text-on-surface/80">
                Volume sur les {weekNumbers.length} semaines
              </p>
              <div className="flex items-center gap-4 text-[11px] text-on-surface-variant/60">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: B1, opacity: 0.8 }} />
                  Séries (vol.)
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-5 border-t-2 border-dashed" style={{ borderColor: B2 }} />
                  RPE moyen
                </span>
              </div>
            </div>

            <svg viewBox="0 0 100 52" className="w-full" style={{ height: 90 }} preserveAspectRatio="none">
              {stats.chartBars.map((b, i) => (
                <rect key={i} x={b.x} y={52 - b.h} width={stats.barW} height={b.h} rx="1.5"
                  fill={colorOfWeek(weekNumbers[i])} opacity="0.82" />
              ))}
              {weekNumbers.length > 1 && (
                <path d={stats.rpeLineD} fill="none" stroke={B2} strokeWidth="1.4"
                  strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2 1.5" />
              )}
              {stats.rpePts.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="1.8" fill={B2} />
              ))}
            </svg>

            <div className="mt-1 hidden sm:flex">
              {stats.chartBars.map((b, i) => (
                <div key={i} className="flex-1 text-center">
                  <p className="text-[10px] font-bold text-on-surface/80">{b.sets} sér.</p>
                  <p className="text-[10px] text-on-surface-variant/60">RPE {b.rpe.toFixed(1)}</p>
                </div>
              ))}
            </div>

            <div className="mt-2 flex">
              {weekNumbers.map((w) => (
                <div key={w} className="flex flex-1 flex-col items-center gap-0.5">
                  <span className="text-[10px] font-medium text-on-surface-variant/60">S{w}</span>
                  <span className="rounded-md px-1 py-0.5 text-[10px] font-bold"
                    style={{ backgroundColor: `${colorOfWeek(w)}20`, color: colorOfWeek(w) }}>
                    B{blockForWeek(blocks, w).index}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Semaines en onglets horizontaux : les empiler verticalement obligeait à
              scroller 8 fois les mêmes séances (elles ne changent pas dans un bloc). */}
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60">
              Semaines
            </p>
            <div className="flex flex-wrap gap-2">
              {weekNumbers.map((week) => {
                const active = week === selectedWeek;
                const bc = colorOfWeek(week);
                const block = blockForWeek(blocks, week);
                return (
                  <button
                    key={week}
                    type="button"
                    onClick={() => setSelectedWeek(week)}
                    aria-pressed={active}
                    className="flex min-w-[52px] flex-col items-center gap-1 rounded-xl border px-2.5 py-1.5 transition-colors md:min-w-[64px] md:px-3 md:py-2"
                    style={{
                      borderColor: active ? bc : "hsl(var(--outline-variant) / 0.35)",
                      backgroundColor: active ? `${bc}18` : "transparent",
                    }}
                  >
                    <span className="text-xs font-bold" style={{ color: active ? bc : undefined }}>
                      <span className={active ? "" : "text-on-surface-variant"}>S{week}</span>
                    </span>
                    <span className="text-[10px] font-bold" style={{ color: bc, opacity: active ? 1 : 0.55 }}>
                      B{block.index}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Séances de la semaine sélectionnée */}
          {[selectedWeek].map((week) => (
            <div key={week}>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60">
                Semaine {week} · {blockForWeek(blocks, week).focus}
              </p>
              <div className="space-y-2">
                {program.sessions.filter((s) => s.week_number === week).map((session) => {
                  const key = `${session.week_number}-${session.day_of_week}`;
                  const isOpen = expanded.has(key);
                  const sessSets = session.exercises.reduce((sum, e) => sum + e.sets_target, 0);
                  const bc = colorOfWeek(week);

                  return (
                    <div key={key} className="overflow-hidden rounded-2xl border border-outline-variant/20">
                      <button
                        type="button"
                        onClick={() => setExpanded((prev) => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key); else next.add(key);
                          return next;
                        })}
                        aria-expanded={isOpen}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-container-low/40"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                          style={{ backgroundColor: `${bc}18` }}>
                          <span className="text-[11px] font-extrabold" style={{ color: bc }}>
                            {DAY_SHORT[session.day_of_week]}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="block text-sm font-bold leading-snug text-on-surface sm:truncate">{session.name}</span>
                          <span className="text-[11px] text-on-surface-variant/60">
                            {session.exercises.length} ex · {sessSets} séries · {session.estimated_duration_min} min
                          </span>
                        </div>
                        {isOpen
                          ? <ChevronUp size={13} className="shrink-0 text-on-surface-variant/40" />
                          : <ChevronDown size={13} className="shrink-0 text-on-surface-variant/40" />}
                      </button>

                      {isOpen && (
                        <div className="border-t border-outline-variant/20">
                          {session.exercises.map((ex, i) => (
                            <div key={i}
                              className="flex items-center gap-3 border-b border-outline-variant/10 px-4 py-2.5 last:border-b-0">
                              <span className="w-5 shrink-0 font-mono text-[11px] text-on-surface-variant/40">
                                {i + 1}
                              </span>
                              {/* mobile : le nom complet passe à la ligne plutôt que d'être tronqué */}
                              <span className="min-w-0 flex-1 text-sm leading-snug text-on-surface sm:truncate">
                                {ex.exercise_name}
                              </span>
                              <span className="shrink-0 font-mono text-[11px] text-on-surface-variant">
                                {ex.sets_target} × {ex.reps_min === ex.reps_max ? ex.reps_min : `${ex.reps_min}–${ex.reps_max}`}
                              </span>
                              <span className="shrink-0 rounded-md px-1.5 py-0.5 font-mono text-[11px] font-bold"
                                style={{ backgroundColor: `${bc}18`, color: bc }}>
                                RPE {ex.rpe_target}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* ── Colonne de droite : structure, volume musculaire, charge ── */}
        <div className="w-full shrink-0 space-y-6 border-t border-outline-variant/20 px-4 py-5 md:px-7 lg:w-64 lg:border-l lg:border-t-0 lg:px-5">

          {/* Structure */}
          <div>
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60">Structure</p>
            <div className="space-y-2.5">
              {[
                { label: "Séances / sem.", value: String(stats.sessionsWk1) },
                { label: "Durée / séance", value: `~${stats.avgDuration} min` },
                { label: "Repos moyen", value: stats.avgRestSec >= 60 ? `~${Math.round(stats.avgRestSec / 60)} min` : `~${Math.round(stats.avgRestSec)}s` },
                { label: "Total exercices", value: String(stats.uniqueExercises) },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-xs text-on-surface-variant/60">{label}</span>
                  <span className="text-xs font-semibold text-on-surface">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Volume musculaire — une couleur par muscle, noms en français */}
          {(stats.topPrimary.length > 0 || stats.topSecondary.length > 0) && (
            <div>
              <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60">
                Volume musculaire{weekNumbers.length > 1 ? " (moy./sem.)" : ""}
              </p>

              {stats.topPrimary.length > 0 && (
                <div className="mb-3">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60">Muscles ciblés</p>
                  <div className="space-y-2">
                    {stats.topPrimary.map(([mg, sets]) => (
                      <div key={mg}>
                        <div className="mb-0.5 flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-[11px] font-medium text-on-surface/70">
                            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: muscleColor(mg) }} />
                            {muscleLabel(mg)}
                          </span>
                          <span className="text-[11px] font-bold text-on-surface">{sets} sér{perWeek}</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-low">
                          <div className="h-full rounded-full"
                            style={{ backgroundColor: muscleColor(mg), width: `${(sets / maxPrimary) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stats.topSecondary.length > 0 && (
                <div>
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60">En support</p>
                  <div className="space-y-1.5">
                    {stats.topSecondary.map(([mg, sets]) => (
                      <div key={mg}>
                        <div className="mb-0.5 flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-[11px] text-on-surface-variant/60">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full opacity-60" style={{ backgroundColor: muscleColor(mg) }} />
                            {muscleLabel(mg)}
                          </span>
                          <span className="text-[11px] text-on-surface-variant/60">{sets} sér{perWeek}</span>
                        </div>
                        <div className="h-1 overflow-hidden rounded-full bg-surface-container-low">
                          <div className="h-full rounded-full opacity-50"
                            style={{ backgroundColor: muscleColor(mg), width: `${(sets / maxSecondary) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Charge / semaine */}
          <div>
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60">Charge / semaine</p>
            <div className="space-y-3.5">
              <div>
                <div className="mb-1.5 flex justify-between">
                  <span className="text-xs text-on-surface-variant/60">Volume total</span>
                  <span className="text-xs font-semibold text-on-surface">
                    {Math.round(stats.totalSetsAll / (weekNumbers.length || 1))} sér.
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-low">
                  <div className="h-full rounded-full" style={{ backgroundColor: B1, width: "72%" }} />
                </div>
              </div>
              <div>
                <div className="mb-1.5 flex justify-between">
                  <span className="text-xs text-on-surface-variant/60">Intensité moyenne</span>
                  <span className="text-xs font-semibold text-on-surface">RPE {stats.avgRPE.toFixed(1)}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-low">
                  <div className="h-full rounded-full"
                    style={{ backgroundColor: B2, width: `${Math.min(100, ((stats.avgRPE - 5) / 5) * 100)}%` }} />
                </div>
              </div>
              <div>
                <div className="mb-1.5 flex justify-between">
                  <span className="text-xs text-on-surface-variant/60">Densité (sér/min)</span>
                  <span className="text-xs font-semibold text-on-surface">{stats.density.toFixed(2)}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-low">
                  <div className="h-full rounded-full bg-emerald-400"
                    style={{ width: `${Math.min(100, stats.density * 250)}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramPreview;
